// components/landing/FeaturesShowcase/FeaturesShowcase.jsx
import { useState, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Container, Row, Col } from 'react-bootstrap';
import { ThemeContext } from '../../../ThemeContext';
import './FeaturesShowcase.scss';

// Import images
import ideLight from './../../../assets/white_home.png';
import ideDark from './../../../assets/white_home.png';
import apiLight from './../../../assets/white_home.png';
import apiDark from './../../../assets/white_home.png';
import schedulingLight from './../../../assets/white_home.png';
import schedulingDark from './../../../assets/white_home.png';
import versioningLight from './../../../assets/white_home.png';
import versioningDark from './../../../assets/white_home.png';

// Custom Icons Components with clean, smooth colors
const VisualIDEIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="14" rx="2" stroke="#6366F1" strokeWidth="2" fill="none"/>
    <path d="M7 7h10v2H7V7zm0 4h6v2H7v-2z" fill="#6366F1"/>
    <circle cx="19" cy="19" r="2" fill="#8B5CF6"/>
    <circle cx="15" cy="19" r="1" fill="#A855F7"/>
  </svg>
);

const APIIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#10B981" strokeWidth="2" fill="rgba(16, 185, 129, 0.1)"/>
    <path d="M2 17l10 5 10-5" stroke="#10B981" strokeWidth="2"/>
    <path d="M2 12l10 5 10-5" stroke="#10B981" strokeWidth="2"/>
    <circle cx="12" cy="7" r="1.5" fill="#059669"/>
    <circle cx="7" cy="9.5" r="1" fill="#34D399"/>
    <circle cx="17" cy="9.5" r="1" fill="#34D399"/>
  </svg>
);

const SchedulingIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="16" rx="2" stroke="#F59E0B" strokeWidth="2" fill="none"/>
    <path d="M16 2v4M8 2v4M3 10h18" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="8" cy="14" r="1" fill="#F97316"/>
    <circle cx="12" cy="14" r="1" fill="#FB923C"/>
    <circle cx="16" cy="14" r="1" fill="#FDBA74"/>
    <rect x="6" y="17" width="4" height="1" fill="#F59E0B"/>
    <rect x="14" y="17" width="4" height="1" fill="#FBBF24"/>
  </svg>
);

const VersioningIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#EF4444" strokeWidth="2" fill="none"/>
    <path d="M14 2v6h6" stroke="#EF4444" strokeWidth="2" fill="none"/>
    <circle cx="9" cy="13" r="1" fill="#DC2626"/>
    <circle cx="12" cy="13" r="1" fill="#F87171"/>
    <circle cx="15" cy="13" r="1" fill="#FCA5A5"/>
    <path d="M8 16h8M8 18h6" stroke="#EF4444" strokeWidth="1" opacity="0.6"/>
    <rect x="7" y="10" width="2" height="1" fill="#B91C1C"/>
    <rect x="10" y="10" width="3" height="1" fill="#EF4444"/>
    <rect x="14" y="10" width="2" height="1" fill="#F87171"/>
  </svg>
);

const features = [
  {
    id: 'visual-ide',
    title: 'Visual IDE',
    subtitle: 'Design AI Agents with Drag & Drop',
    description:
      'Build complex agent workflows visually using our React-Flow based IDE. No coding required - just connect nodes to create sophisticated AI logic.',
    highlights: [
      '32+ pre-built node types',
      'Real-time validation',
      'Copy-paste sub-flows',
      'Cross-platform (Web + Desktop)',
    ],
    stats: [
      { label: 'Node Types', value: '32+' },
      { label: 'Learning Time', value: '< 1hr' },
    ],
    lightImage: ideLight,
    darkImage: ideDark,
    icon: VisualIDEIcon,
    color: '#6366F1',
  },
  {
    id: 'api-programs',
    title: 'API Programs',
    subtitle: 'Secure REST Integration Made Simple',
    description:
      'Create versioned, reusable API integrations with built-in secret management. Connect to any REST service with our Model-Context-Protocol architecture.',
    highlights: [
      'Encrypted secret storage',
      'Version control built-in',
      'Template parameters',
      'Public/Private visibility',
    ],
    stats: [
      { label: 'Pre-built Programs', value: '50+' },
      { label: 'Security Level', value: 'AES-256' },
    ],
    lightImage: apiLight,
    darkImage: apiDark,
    icon: APIIcon,
    color: '#10B981',
  },
  {
    id: 'scheduling',
    title: 'Agent Scheduling',
    subtitle: 'Automate with Cron-like Precision',
    description:
      'Schedule your agents to run automatically - from every minute to monthly. Perfect for reports, data syncing, and automated workflows.',
    highlights: [
      'Cron-based scheduling',
      'Time-zone aware',
      'Execution logs',
      'Pause/Resume anytime',
    ],
    stats: [
      { label: 'Min Interval', value: '1min' },
      { label: 'Uptime', value: '99.9%' },
    ],
    lightImage: schedulingLight,
    darkImage: schedulingDark,
    icon: SchedulingIcon,
    color: '#F59E0B',
  },
  {
    id: 'versioning',
    title: 'Version Control',
    subtitle: 'Immutable Releases, Reproducible Results',
    description:
      'Every agent and API program is versioned with semantic versioning. Published versions are immutable, ensuring consistent behavior across all deployments.',
    highlights: [
      'Semantic versioning',
      'Immutable releases',
      'Full metadata per version',
      'Backward compatibility',
    ],
    stats: [
      { label: 'Version Types', value: '3 Types' },
      { label: 'Rollback Speed', value: 'Instant' },
    ],
    lightImage: versioningLight,
    darkImage: versioningDark,
    icon: VersioningIcon,
    color: '#EF4444',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

const FeaturesShowcase = () => {
  const { theme } = useContext(ThemeContext);
  const [activeFeature, setActiveFeature] = useState('visual-ide');
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const currentFeature =
    features.find((f) => f.id === activeFeature) || features[0];
  const currentImage =
    theme === 'dark' ? currentFeature.darkImage : currentFeature.lightImage;

  // Handle feature change with smooth transition
  const handleFeatureChange = (featureId) => {
    if (featureId === activeFeature) return;
    setIsTransitioning(true);
    setImageLoaded(false);
    setTimeout(() => {
      setActiveFeature(featureId);
      setIsTransitioning(false);
    }, 150);
  };

  // Keyboard navigation support
  const handleKeyDown = (e, featureId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleFeatureChange(featureId);
    }
  };

  return (
    <section className="features-showcase-section py-5" id="features-showcase">
      <Container>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
        >
          {/* Section Header */}
          <Row className="mb-5 pb-3">
            <Col lg={10} xl={8} className="mx-auto text-center">
              <motion.div variants={itemVariants}>
                <h2 className="section-title mb-4">
                  Everything You Need to Build
                  <span className="text-gradient"> Next-Gen AI Agents</span>
                </h2>
                <p className="section-subtitle text-secondary">
                  From visual design to automated deployment, Quartermaster
                  provides a complete toolkit for creating, managing, and
                  scaling AI agents.
                </p>
              </motion.div>
            </Col>
          </Row>

          {/* Feature Tabs */}
          <Row className="mb-5">
            <Col>
              <motion.div
                className="feature-tabs d-flex flex-wrap justify-content-center gap-3"
                variants={itemVariants}
                role="tablist"
                aria-label="Feature selection"
              >
                {features.map((feature, index) => {
                  const IconComponent = feature.icon;
                  return (
                    <button
                      key={feature.id}
                      className={`feature-tab ${
                        activeFeature === feature.id ? 'active' : ''
                      }`}
                      onClick={() => handleFeatureChange(feature.id)}
                      onKeyDown={(e) => handleKeyDown(e, feature.id)}
                      role="tab"
                      aria-selected={activeFeature === feature.id}
                      aria-controls={`feature-panel-${feature.id}`}
                      tabIndex={activeFeature === feature.id ? 0 : -1}
                    >
                      <IconComponent className="tab-icon" aria-hidden="true" />
                      <span className="tab-title">{feature.title}</span>
                      {activeFeature === feature.id && (
                        <motion.div
                          className="tab-indicator"
                          layoutId="activeTab"
                          transition={{
                            type: 'spring',
                            stiffness: 380,
                            damping: 30,
                          }}
                        />
                      )}
                    </button>
                  );
                })}
              </motion.div>
            </Col>
          </Row>

          {/* Feature Content */}
          <Row className="align-items-center feature-content-row">
            {/* Feature Details */}
            <Col lg={5} className="mb-4 mb-lg-0">
              <div
                className="feature-content"
                aria-live="polite"
                role="tabpanel"
                id={`feature-panel-${activeFeature}`}
                aria-labelledby={activeFeature}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeFeature}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{
                      duration: 0.3,
                      ease: [0.25, 0.46, 0.45, 0.94],
                    }}
                  >
                    <div className="feature-header mb-4">
                      <div className="feature-title-wrapper d-flex align-items-center gap-3">
                        <div
                          className="feature-emoji"
                          style={{
                            color: currentFeature.color,
                          }}
                        >
                          <currentFeature.icon className="custom-feature-icon" />
                        </div>
                        <div className="feature-text-content">
                          <h3 className="feature-title-main mb-2">
                            {currentFeature.title}
                          </h3>
                          <p className="feature-subtitle-main text-secondary mb-0">
                            {currentFeature.subtitle}
                          </p>
                        </div>
                      </div>
                    </div>

                    <p className="feature-description mb-4">
                      {currentFeature.description}
                    </p>

                    {/* Highlights */}
                    <div className="feature-highlights mb-4" role="list">
                      {currentFeature.highlights.map((highlight, index) => (
                        <motion.div
                          key={index}
                          className="highlight-item d-flex align-items-start mb-3"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            delay: index * 0.08,
                            duration: 0.4,
                            ease: [0.25, 0.46, 0.45, 0.94],
                          }}
                          role="listitem"
                        >
                          <span className="highlight-check" aria-hidden="true">
                            ✓
                          </span>
                          <span className="highlight-text">{highlight}</span>
                        </motion.div>
                      ))}
                    </div>

                    {/* Enhanced Stats Section */}
                    <div className="feature-stats-enhanced">
                      <div className="row g-3">
                        {currentFeature.stats.map((stat, index) => (
                          <div key={index} className="col-6">
                            <motion.div
                              className="stat-card-enhanced"
                              initial={{ opacity: 0, scale: 0.9, y: 20 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              transition={{
                                delay: 0.3 + index * 0.1,
                                duration: 0.4,
                                ease: [0.25, 0.46, 0.45, 0.94],
                              }}
                              whileHover={{
                                scale: 1.02,
                                y: -2,
                                transition: { duration: 0.2 }
                              }}
                            >
                              <div className="stat-number">{stat.value}</div>
                              <div className="stat-description">{stat.label}</div>
                            </motion.div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </Col>

            {/* Feature Visual */}
            <Col lg={7}>
              <div
                className="feature-visual"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <div
                  className="visual-container"
                  data-feature={currentFeature.id}
                >
                  <div className="visual-header">
                    <div className="visual-controls" role="presentation">
                      <span
                        className="control-dot red"
                        aria-hidden="true"
                      ></span>
                      <span
                        className="control-dot yellow"
                        aria-hidden="true"
                      ></span>
                      <span
                        className="control-dot green"
                        aria-hidden="true"
                      ></span>
                    </div>
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={currentFeature.id}
                        className="visual-title"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3 }}
                      >
                        {currentFeature.title}
                      </motion.span>
                    </AnimatePresence>
                  </div>
                  <div className="visual-content">
                    {/* Image Skeleton Loader */}
                    <AnimatePresence>
                      {!imageLoaded && (
                        <motion.div
                          className="image-skeleton"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <motion.div
                            className="skeleton-shimmer"
                            animate={{ x: ['-100%', '100%'] }}
                            transition={{
                              repeat: Infinity,
                              duration: 1.5,
                              ease: 'easeInOut',
                            }}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <AnimatePresence mode="wait">
                      <motion.img
                        key={currentFeature.id}
                        src={currentImage}
                        alt={`${currentFeature.title} - ${currentFeature.subtitle}`}
                        className="feature-image"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{
                          opacity: 1,
                          scale: 1,
                        }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{
                          duration: 0.4,
                          ease: [0.25, 0.46, 0.45, 0.94],
                        }}
                        onLoad={() => setImageLoaded(true)}
                        loading="lazy"
                      />
                    </AnimatePresence>
                    {/* Overlay gradient for depth */}
                    <motion.div
                      className="visual-overlay"
                      animate={{ opacity: isHovered ? 0.8 : 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </motion.div>
      </Container>
    </section>
  );
};

export default FeaturesShowcase;